import React from 'react';
import { useTheme } from '@mui/material/styles';
import { useSubscription } from '../contexts/SubscriptionProvider';

/**
 * Shows remaining usage for a rate-limited feature.
 * Only visible for plans that have limits (e.g., Starter).
 * Renders nothing for unlimited plans.
 *
 * Usage:
 *   <UsageIndicator feature={FEATURES.AI_CHAT} />
 */
export const UsageIndicator = ({ feature }) => {
    const { getUsage } = useSubscription();
    const theme = useTheme();
    const usage = getUsage(feature);

    // Don't show for unlimited plans or if no usage data
    if (!usage || usage.limit === 0) return null;

    const percentage = Math.min((usage.used / usage.limit) * 100, 100);
    const isAtLimit = usage.used >= usage.limit;
    const isNearLimit = percentage >= 80;
    const isDark = theme.palette.mode === 'dark';

    let style = isDark
        ? { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }
        : { background: 'rgba(148,163,184,0.2)', color: 'rgba(100,116,139,0.8)' };
    if (isAtLimit) {
        style = isDark
            ? { background: 'rgba(239,68,68,0.12)', color: '#f87171' }
            : { background: '#fee2e2', color: '#dc2626' };
    } else if (isNearLimit) {
        style = isDark
            ? { background: 'rgba(245,158,11,0.12)', color: '#fbbf24' }
            : { background: '#fef3c7', color: '#d97706' };
    }

    return (
        <div
            style={{
                ...style,
                display: 'inline-flex',
                fontSize: '11px',
                fontWeight: 500,
                padding: '3px 10px',
                borderRadius: '9999px',
            }}
        >
            {isAtLimit
                ? 'Limit reached'
                : `${usage.remaining} left this month`
            }
        </div>
    );
};
